import { action } from 'mobx';
import {
    POWER_REVIEWS_SCRIPT,
    REVIEW_COMPONENTS,
    REVIEW_SNAPSHOT_ID
} from '../constants/PowerReviews';
import { IReviewDetailsData, ReviewDetails } from '../domain/entities/ReviewDetails';
import { IReviewDetailsConfig } from '../../exported/review-details/ReviewDetails';
import { ConfigStore } from '../../service-setup/ConfigStore';
import { RatingsReviewsBaseController } from './RatingsReviewsBaseController';
import { emit, events } from 'elc-service-bus';
import { REVIEW_CATEGORY } from '../constants/ServiceReviews';
import { IPowerReviews } from '../interfaces/IPowerReviews';

declare let POWERREVIEWS: IPowerReviews;

interface IReviewDetailsControllerData {
    config: IReviewDetailsConfig;
    reviewDetailsRef: React.RefObject<HTMLDivElement>;
}

export class ReviewDetailsViewController extends RatingsReviewsBaseController {
    private isReviewLoaded: boolean;
    private reviewDetailsData: IReviewDetailsData;
    private configs: ConfigStore;
    private reviewDetailsRef: React.RefObject<HTMLDivElement>;
    private isProductInfoVisible: boolean;

    constructor(configStore: ConfigStore) {
        super();
        this.configs = configStore;
    }

    @action
    public loaded = async (props: IReviewDetailsControllerData) => {
        this.reviewDetailsData = props.config;
        const payload = {
            id: this.reviewDetailsData.pageId,
            name: this.reviewDetailsData.name,
            reviewCategory: REVIEW_CATEGORY
        };
        this.reviewDetailsRef = props.reviewDetailsRef;
        this.isProductInfoVisible = props.config.showPageInfo;
        this.showReviewOverlay();
        this.addReviewsEventHandlers(payload);
        emit(events.REVIEW_OVERLAY_OPENED, payload);
    };

    private showReviewOverlay() {
        if (this.isReviewLoaded) {
            this.renderOverlay();

            return;
        }
        this.loadPowerReviews();
    }

    private loadPowerReviews() {
        const script = document.getElementsByTagName('script')[0];
        const powerReviewsScript = document.createElement('script');
        powerReviewsScript.type = 'text/javascript';
        powerReviewsScript.async = true;
        powerReviewsScript.src = POWER_REVIEWS_SCRIPT;
        powerReviewsScript.onload = () => {
            this.isReviewLoaded = true;
            this.showReviewOverlay();
        };

        script.parentNode && script.parentNode.insertBefore(powerReviewsScript, script);
    }

    private renderOverlay() {
        const reviewsConfig = {
            ...this.configs.config.ratingsReviewsConfig,
            components: REVIEW_COMPONENTS
        };
        const reviewDisplayParams = ReviewDetails.toJson({
            ...reviewsConfig,
            ...this.reviewDetailsData
        });
        POWERREVIEWS.display.render(reviewDisplayParams);
        this.isProductInfoVisible && this.observeReviewDetails();
    }

    private observeReviewDetails = () => {
        const reviewDetailsContainer = this.reviewDetailsRef.current;
        if (reviewDetailsContainer === null) {
            return;
        }
        const mutationObserver: MutationObserver = new MutationObserver(this.onReviewDetailsLoaded);
        const config = {
            childList: true,
            subtree: true
        };

        mutationObserver.observe(reviewDetailsContainer, config);
    };

    private onReviewDetailsLoaded = (
        mutationList: MutationRecord[],
        mutationObserver: MutationObserver
    ) => {
        for (const mutation of mutationList) {
            if ((<Element>mutation.target).querySelector(`#${REVIEW_SNAPSHOT_ID}`)) {
                this.appendProductDetails();
                mutationObserver.disconnect();
                break;
            }
        }
    };

    // Hacky code to inject product/service info into review details as Power Reviews doesn't support this
    private appendProductDetails() {
        const reviewSnapShotContainer = document.getElementById(REVIEW_SNAPSHOT_ID);
        if (!reviewSnapShotContainer) {
            return;
        }
        const imageElement = document.createElement('img');
        const productElement = document.createElement('p');
        const descriptionElement = document.createElement('p');
        const productInfoWrapper = document.createElement('div');

        imageElement.src = this.reviewDetailsData.imageUrl;
        imageElement.alt = this.reviewDetailsData.name;
        imageElement.className = 'elc-pr-review-product-image';

        productElement.innerHTML = this.reviewDetailsData.name;
        productElement.className = 'elc-pr-review-product-name';

        descriptionElement.innerHTML = this.reviewDetailsData.description;
        descriptionElement.className = 'elc-pr-review-product-description';

        productInfoWrapper.className = 'elc-pr-review-product-wrapper';
        productInfoWrapper.appendChild(productElement);
        productInfoWrapper.appendChild(descriptionElement);

        reviewSnapShotContainer.prepend(imageElement, productInfoWrapper);
    }

    public handleUnmount() {
        this.removeReviewsEventHandlers();
        emit(events.REVIEW_OVERLAY_CLOSED, {
            id: this.reviewDetailsData.pageId,
            name: this.reviewDetailsData.name || '',
            reviewCategory: 'service reviews'
        });
    }
}
